import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import { formatCurrency, formatDate } from '@/lib/format'
import type { Company } from '@/types/api'
import type { ReactNode } from 'react'

interface InvestmentSectionProps {
  company: Company
}

export function InvestmentSection({ company }: InvestmentSectionProps) {
  return (
    <div className="space-y-3.5 border-b border-primary/10 pb-3">
      <div className="flex flex-wrap items-baseline justify-between gap-1.5">
        <Typography variant="h6">Investment</Typography>
        <Typography variant="body-sm" className="text-primary/80">
          Initial Inv Date:{' '}
          {company.initialInvestmentDate
            ? formatDate(company.initialInvestmentDate)
            : '—'}
        </Typography>
      </div>

      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        <Card label="Total Ownership">
          <Typography
            variant="body"
            as="p"
            className="text-primary tabular-nums"
          >
            {company.ownershipPercentage !== undefined &&
            company.ownershipPercentage !== null
              ? `${company.ownershipPercentage}%`
              : '-'}
          </Typography>
        </Card>

        <Card label="Fund">
          <div className="flex flex-wrap gap-1">
            {company.funds.length > 0 ? (
              [...company.funds]
                .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation))
                .map((fund) => (
                  <Tooltip key={fund.id}>
                    <TooltipTrigger>
                      <Badge className="rounded-sm">{fund.abbreviation}</Badge>
                    </TooltipTrigger>
                    <TooltipContent>{fund.displayName}</TooltipContent>
                  </Tooltip>
                ))
            ) : (
              <Typography variant="body" as="span" className="text-primary">
                —
              </Typography>
            )}
          </div>
        </Card>

        <Card label="Total Invested">
          <Typography
            variant="body"
            as="p"
            className={cn(
              'tabular-nums',
              company.investedAmount != null && company.investedAmount < 0
                ? 'text-destructive'
                : 'text-primary'
            )}
          >
            {formatCurrency(company.investedAmount)}
          </Typography>
        </Card>

        <Card label="Current Equity Value">
          <Typography
            variant="body"
            as="p"
            className={cn(
              'tabular-nums',
              company.currentValue != null && company.currentValue < 0
                ? 'text-destructive'
                : 'text-primary'
            )}
          >
            {formatCurrency(company.currentValue)}
          </Typography>
        </Card>
      </div>
    </div>
  )
}

interface CardProps {
  label: string
  children: ReactNode
}

function Card({ label, children }: CardProps) {
  return (
    <div className="flex flex-col justify-between rounded-md border border-primary/20 bg-white/50 pl-2 pr-1.5 pt-3 pb-2 dark:bg-gray-900">
      <Typography variant="body-sm" className="mb-1.5 text-primary/80">
        {label}
      </Typography>
      {children}
    </div>
  )
}
